import {useImpersonateMutation} from "@/api/miscApi";
import Button from "@/components/Common/Button";
import InputField from "@/components/Common/forms/InputField";
import type {TokenClaimsSerializer} from "@/generated/DjangoTypes";
import {brandImpersonationAtom} from "@/store/jotaiAtoms";
import {useTokenStore} from "@/store/token";
import {switchSession} from "@/utils/impersonationUtils";
import {createFileRoute} from "@tanstack/react-router";
import {useSetAtom} from "jotai";
import {jwtDecode} from "jwt-decode";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";

export const Route = createFileRoute("/_authenticated/_admin/admins/impersonate")({
  component: Impersonate,
});

function Impersonate() {
  const {t} = useTranslation("impersonate");

  useTitle(t("title"));

  const [email, setEmail] = useState("");
  const setBrandImpersonation = useSetAtom(brandImpersonationAtom);
  const {mutate, isPending} = useImpersonateMutation();

  const handleImpersonate = () => {
    mutate(
      {email: email.trim()},
      {
        onSuccess: ({data}) => {
          const {access, refresh} = useTokenStore.getState();

          const claims = jwtDecode<TokenClaimsSerializer>(data.access);
          const agencyId = claims.access_level === "AGENCY" ? claims.agency_ids?.[0] : undefined;
          const companyId = claims.sub_brand_ids?.[0];
          const target = agencyId ? {agencyId} : companyId ? {companyId} : null;

          if (target) {
            setBrandImpersonation({adminAccess: access!, adminRefresh: refresh!, ...target});
          }
          switchSession(data);
        },
      },
    );
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="border-neutral-grey-light flex w-full max-w-md flex-col gap-5 rounded-xl border bg-white p-6">
        <h1 className="text-2.5xl font-semibold">{t("title")}</h1>
        <p className="bg-pretty-pink-200 text-danger-dark-red rounded-md px-4 py-3 text-sm font-medium">
          {t("warning")}
        </p>
        <InputField
          autoFocus
          type="email"
          label={t("email_label")}
          placeholder={t("email_placeholder")}
          value={email}
          onChange={setEmail}
          onKeyDown={e => e.key === "Enter" && !!email.trim() && !isPending && handleImpersonate()}
        />
        <Button buttonStyle="Blue" onClick={handleImpersonate} isDisabled={!email.trim()} isLoading={isPending}>
          {t("impersonate")}
        </Button>
      </div>
    </div>
  );
}
